import { getRouteApi } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import EditIcon from '@/assets/icons/edit.svg?react'
import DataBlock from '@/components/DataBlock/DataBlock'
import Tag from '@/components/Tag/Tag'
import type { Action } from '@/components/common/BasicLayout/Actions'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import useAuthStore from '@/store/useAuthStore'
import { formatDate } from '@/utils/format-date'

import styles from './SchoolBreakDetailsView.module.scss'
import { schoolBreakStatusToTagMap } from '../constants/school-break-status-to-tag-map'
import { useSchoolBreak } from '../queries/useSchoolBreak'

const routeApi = getRouteApi('/_auth/school-settings/breaks/$breakId/')

const SchoolBreakDetailsView = () => {
  const { user } = useAuthStore()

  const { t } = useTranslation('schoolSettings')

  const { breakId } = routeApi.useParams()
  const { data: breakData } = useSchoolBreak(breakId)

  if (!breakData) return null

  const schoolBreakInfo = [
    { label: t('label.name'), value: breakData.name },
    {
      label: t('label.start-date'),
      value: formatDate(breakData.startDate)
    },
    {
      label: t('label.end-date'),
      value: formatDate(breakData.endDate)
    }
  ]

  const actions: Action[] = [
    ...(user?.isSuperAdmin
      ? ([
          {
            text: t('button.edit'),
            icon: <EditIcon />,
            asLink: true,
            to: '/school-settings/breaks/$breakId/edit',
            params: {
              breakId
            },
            hidden: breakData.status === 'past'
          }
        ] satisfies Action[])
      : [])
  ]

  return (
    <BasicLayout
      breadcrumbs={[
        {
          title: t('header.school-settings'),
          to: '/school-settings'
        },
        {
          title: breakData.name ?? ''
        }
      ]}
      actions={actions}
      subheader={t('header.school-break-details')}
      header={
        <div className={styles.headerWrapper}>
          <h1 className={styles.header}>{breakData.name}</h1>
          <Tag {...schoolBreakStatusToTagMap(t)[breakData.status]} />
        </div>
      }
      moduleName={t('header.administration', { ns: 'common' })}
    >
      <section className={styles.section}>
        <h2 className={styles.sectionHeader}>{t('header.details')}</h2>

        <DataBlock
          header={t('header.school-break')}
          infoArray={schoolBreakInfo}
        />
      </section>
    </BasicLayout>
  )
}

export default SchoolBreakDetailsView
